import { useState } from 'react';

import type { ExpandedMode } from '../components/GovernanceApproverInbox/ApproveActions';
import EmptyState from '../components/GovernanceApproverInbox/EmptyState';
import GovernanceInboxRow, { type GovernanceQueueRow } from './GovernanceInboxRow';

interface GovernanceInboxProps {
  queue: GovernanceQueueRow[];
  /**
   * Pinned reference for each row's age-in-queue. Production leaves it unset
   * and every row reads the wall clock; stories and tests pin it so a relative
   * label is a fixed string rather than one that drifts with the suite.
   */
  now?: Date;
}

/**
 * The governance approver's queue — presentation only.
 *
 * Both sides are now live: `myGovernanceQueue` (gap G3, ENG-5604) closed the
 * read half, and `ApprovalsPage` is the container that fetches it. It stayed a
 * dumb component through that change because the queue is viewer-scoped
 * server-side, so there was never any client-side filtering to move in here —
 * which is also why the alternative (assembling the queue from `engagements` /
 * `engagementsByProject` with an N+1 `engagement(id)` per candidate) was worth
 * refusing rather than building and then deleting.
 *
 * `queue` stays a prop so `governance.test.tsx` can drive the decisions, the
 * expanders and the error mapping without a mocked network.
 */
export default function GovernanceInbox({ queue, now }: GovernanceInboxProps): JSX.Element {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [expandedMode, setExpandedMode] = useState<ExpandedMode | null>(null);
  const [decidedIds, setDecidedIds] = useState<string[]>([]);

  const visible = queue.filter((row) => !decidedIds.includes(row.engagementId));

  function handleExpand(rowId: string, mode: ExpandedMode | null) {
    setExpandedRowId(mode === null ? null : rowId);
    setExpandedMode(mode);
  }

  return (
    <div className="mx-auto flex w-full max-w-[920px] flex-col gap-24 px-16 py-24 sm:px-24">
      <header className="flex flex-col gap-4 border-b border-surface-border pb-16">
        <h1 className="text-on-surface hd-3">Approvals</h1>
        <p className="text-on-surface-muted body-2">
          {`${visible.length} ${visible.length === 1 ? 'engagement' : 'engagements'} awaiting your approval`}
        </p>
      </header>

      {visible.length === 0 ? (
        <EmptyState />
      ) : (
        <ul className="flex list-none flex-col gap-12 p-[0]">
          {visible.map((row) => (
            <GovernanceInboxRow
              key={row.engagementId}
              row={row}
              expandedMode={expandedRowId === row.engagementId ? expandedMode : null}
              onExpand={(mode) => handleExpand(row.engagementId, mode)}
              onDecided={(id) => setDecidedIds((prev) => [...prev, id])}
              now={now}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
